import React, { useEffect } from "react";
import styled from "styled-components";
import slugify from "slugify";

import colors from "src/assets/styles/colors.js";
import typographySizes from "src/assets/styles/typographySizes.js";
import mediaQuery from "src/assets/styles/mediaQuery.js";

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10002;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24rem;
  background: rgba(14, 36, 38, 0.55);
  box-sizing: border-box;
`;

const Modal = styled.div`
  position: relative;
  width: 100%;
  max-width: 640rem;
  padding: 40rem 44rem 36rem;
  border: 1rem solid rgba(14, 36, 38, 0.85);
  border-radius: 12rem;
  overflow: hidden;
  background:

    radial-gradient(
      ellipse 130% 120% at 0% 100%,
      ${colors.orange} 0%,
      #f6a85c 25%,
      #f4dca0 100%
    );
  box-sizing: border-box;

  @media (max-width: ${mediaQuery.tablet}) {
    padding: 32rem 24rem 28rem;
  }
`;

const Close = styled.button`
  position: absolute;
  top: 20rem;
  right: 20rem;
  cursor: pointer;
  font-size: 22rem;
  line-height: 1;
  color: ${colors.greenDark};
  background: none;
  transition: opacity 0.3s;

  &:hover {
    opacity: 0.6;
  }
`;

const Title = styled.h2`
  margin: 0 48rem 20rem 0;
  font-size: ${typographySizes.l}rem;
  line-height: ${typographySizes.lh};
  font-weight: 600;
  color: ${colors.greenDark};
`;

const Description = styled.p`
  margin: 0 0 32rem;
  max-width: 520rem;
  font-size: ${typographySizes.s}rem;
  line-height: ${typographySizes.sh};
  color: ${colors.greenDark};
`;

const DescriptionDesktop = styled(Description)`
  @media (max-width: ${mediaQuery.tablet}) {
    display: none;
  }
`;

const DescriptionMobile = styled(Description)`
  display: none;

  @media (max-width: ${mediaQuery.tablet}) {
    display: block;
  }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 24rem;
`;

const PrimaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 14rem 24rem;
  border-radius: 4rem;
  font-size: ${typographySizes.s}rem;
  font-weight: 600;
  color: ${colors.white};
  background: ${colors.greenDark};
  text-decoration: none;
  transition: opacity 0.3s;

  &:hover {
    opacity: 0.85;
  }
`;

const ContinueLink = styled.button`
  cursor: pointer;
  font-size: ${typographySizes.s}rem;
  color:  ${colors.greenDark};
  background: none;
  text-decoration: underline;
  text-underline-offset: 3rem;
  transition: opacity 0.3s;

  &:hover {
    opacity: 0.7;
  }
`;

const CadenivePopup = ({ globalReachPage, lang, onClose }) => {
  const isEn = lang === "en";

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const slug = slugify(globalReachPage, {
    lower: true,
    strict: true,
  });

  return (
    <Overlay onClick={onClose} role="presentation">
      <Modal
        role="dialog"
        aria-modal="true"
        aria-labelledby="cadenive-popup-title"
        onClick={(event) => event.stopPropagation()}
      >
        <Close onClick={onClose} aria-label={isEn ? "Close" : "Zamknij"}>
          ×
        </Close>

        <Title id="cadenive-popup-title">Cadenive</Title>

        <DescriptionDesktop>
          {isEn
            ? "Excalibur are part of Cadenive — a new, independent corporate finance firm. The team and the standards you know, with greater scale and cross-border reach."
            : "Excalibur jest teraz częścią Cadenive — nowa, niezależna firma doradztwa finansowego. Ten sam zespół i standardy, które znasz, z większą skalą i zasięgiem międzynarodowym."}
        </DescriptionDesktop>

        <DescriptionMobile>
          {isEn
            ? "Excalibur are part of Cadenive — the same team and standards, with greater scale and cross-border reach."
            : "Excalibur jest teraz częścią Cadenive — ten sam zespół i standardy, z większą skalą i zasięgiem międzynarodowym."}
        </DescriptionMobile>

        <Actions>
          <PrimaryButton
            href={`/${lang === "en" ? "en/" : ""}${slug}`}
            rel="noopener noreferrer"
            onClick={onClose}
          >
            {isEn ? "About Cadenive →" : "O Cadenive →"}
          </PrimaryButton>
          <ContinueLink type="button" onClick={onClose}>
            {isEn ? "Continue to the site" : "Przejdź do strony"}
          </ContinueLink>
        </Actions>
      </Modal>
    </Overlay>
  );
};

export default CadenivePopup;
